import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import { CustomerContext } from '../../../contexts/CustomerContext';
import useQueryString from '../../../hooks/use-query-string';
import usePastImages from '../../../hooks/usePastImages';
import usePastImagesSeniorQuickOrderPricelist from '../../../hooks/usePastImagesSeniorQuickOrderPricelist';
import { OrderPackage } from '../../../models';
import Sheet from '../../../models/Sheet';
import Spinner from '../../Spinner';
import { routes } from '../../../routes';
import QueryKeys from '../../../utils/QueryKeys';
import PastImageOption from './PastImageOption';
import DiscountPopup from './DiscountPopup';
import styles from './SeniorPastImagesQuickOrder.module.scss';
import { refreshVolumeDiscount } from '../../../api/carts';

// Mirrors Coupon::VOLUME_DISCOUNT_TIERS server-side. Same tier curve as
// the elementary page — actual discount math is computed by the API.
function volumeDiscountPercentage(count) {
  if (count < 2) return 0;
  if (count >= 5) return 40;
  return (count - 1) * 10;
}

function priceForOption(opt) {
  const priceRecord = opt.prices && opt.prices[0];
  return (priceRecord && Number(priceRecord.price)) || 0;
}

// Grade-label inference for the thumbnail strip at the top of the page.
// The folder convention is graduation-year named (e.g. 'fall2026' means
// the student graduates in 2026), so we work backwards from the senior's
// graduation year to figure out each past-photo's grade.
//
// Returns a label like '4th Grade', 'Kindergarten', '8th Grade Grad', etc.
function gradeLabelForFolder(folder, gradYear) {
  if (!folder || !gradYear) return '';
  const m = folder.match(/(\d{4})$/);
  if (!m) return '';
  const folderYear = parseInt(m[1], 10);
  // Senior year = grade 12. Each year prior is one grade earlier.
  // gradYear - folderYear = years remaining when that photo was taken.
  const yearsBack = gradYear - folderYear;
  const grade = 12 - yearsBack;
  if (grade < -1 || grade > 12) return '';
  if (grade === 12) return 'Senior';
  if (grade === 11) return '11th Grade';
  if (grade === 10) return '10th Grade';
  if (grade === 9) return '9th Grade';
  if (grade === 8) return '8th Grade';
  if (grade === 7) return '7th Grade';
  if (grade === 6) return '6th Grade';
  if (grade === 5) return '5th Grade';
  if (grade === 4) return '4th Grade';
  if (grade === 3) return '3rd Grade';
  if (grade === 2) return '2nd Grade';
  if (grade === 1) return '1st Grade';
  if (grade === 0) return 'Kindergarten';
  if (grade === -1) return 'Pre-K';
  return '';
}

export default function SeniorPastImagesQuickOrder() {
  const { customer, setCustomer } = useContext(CustomerContext);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { toReview } = useQueryString();
  const isUpsell = !!toReview;
  const cartKey = customer.priorityCart ? 'priorityCart' : 'cart';
  const cart = customer[cartKey];
  const student = customer.currentStudent;
  const schoolId = student?.schoolId || customer.currentSchool?.id;

  const {
    data: pastImages,
    isLoading: isLoadingImages,
  } = usePastImages({
    studentId: student?.id,
    additionalQuery: cart ? { folder: { not_already_selected_for_cart: cart.id } } : {},
  });

  const {
    data: pricelist,
    isLoading: isLoadingPricelist,
  } = usePastImagesSeniorQuickOrderPricelist(schoolId);

  const isFetching = useIsFetching();
  const [selections, setSelections] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasShownDiscountPopup, setHasShownDiscountPopup] = useState(false);
  const [isDiscountPopupOpen, setIsDiscountPopupOpen] = useState(false);

  const options = (pricelist?.options || []).filter((opt) => priceForOption(opt) > 0);
  const backgrounds = pricelist?.backgroundSet?.backgrounds || [];
  const packagePoses = pricelist?.packagePoses || [];

  const defaultsByStudentImageId = useMemo(() => {
    const map = {};
    (pastImages || []).forEach((si) => {
      const primaryPose = (si.poses && si.poses[0]) || null;
      const number = primaryPose?.number;
      const season = si.folder;

      const match = packagePoses.find((pp) => (
        pp.poseNumber === number && pp.season === season
      ));

      const bg = (match && match.background) || backgrounds[0] || null;
      map[si.id] = bg;
    });
    return map;
  }, [pastImages, packagePoses, backgrounds]);

  // Senior portrait is the image whose folder contains 'senior' — pulled out
  // for the hero strip + the grad-products row up top. Falls back to null
  // if the student doesn't have one (the parent landed here without a
  // current senior portrait — rare but possible).
  const seniorImage = useMemo(() => (
    (pastImages || []).find((si) => (si.folder || '').toLowerCase().includes('senior'))
  ), [pastImages]);

  // For the grade thumbnail strip: chronologically ordered past images with
  // a grade label per image. Excludes the senior portrait (it gets its own
  // hero placement) and excludes sport/sibling/holiday folders.
  const gradedTimeline = useMemo(() => {
    const gradYear = parseInt((seniorImage?.folder || '').match(/(\d{4})$/)?.[1] || '', 10);
    if (!gradYear) return [];
    return (pastImages || [])
      .filter((si) => {
        const folder = (si.folder || '').toLowerCase();
        if (folder.includes('senior')) return false;
        if (folder.includes('sport')) return false;
        if (folder.includes('sibling')) return false;
        if (folder.includes('holiday')) return false;
        if (folder.includes('unenrolled')) return false;
        return true;
      })
      .map((si) => ({
        studentImage: si,
        label: gradeLabelForFolder(si.folder, gradYear),
      }))
      .filter((entry) => entry.label)
      .sort((a, b) => {
        const aYear = parseInt(a.studentImage.folder.match(/(\d{4})$/)?.[1] || '0', 10);
        const bYear = parseInt(b.studentImage.folder.match(/(\d{4})$/)?.[1] || '0', 10);
        return aYear - bYear;
      });
  }, [pastImages, seniorImage]);

  const handleSelectOption = (studentImageId, optionId) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (!optionId) {
        delete next[studentImageId];
        return next;
      }
      const existing = next[studentImageId] || {};
      const defaultBg = defaultsByStudentImageId[studentImageId];
      next[studentImageId] = {
        optionId,
        backgroundId: existing.backgroundId || defaultBg?.id || backgrounds[0]?.id || null,
      };
      return next;
    });
  };

  const handleSelectBackground = (studentImageId, backgroundId) => {
    setSelections((prev) => {
      const existing = prev[studentImageId];
      if (!existing) return prev;
      return { ...prev, [studentImageId]: { ...existing, backgroundId } };
    });
  };

  useEffect(() => {
    if (hasShownDiscountPopup) return;
    if (Object.keys(selections).length === 0) return;
    setIsDiscountPopupOpen(true);
    setHasShownDiscountPopup(true);
  }, [selections, hasShownDiscountPopup]);

  const summary = useMemo(() => {
    const entries = Object.entries(selections);
    const total = entries.reduce((acc, [, sel]) => {
      const opt = options.find((o) => o.id === sel.optionId);
      return acc + priceForOption(opt || {});
    }, 0);
    return { count: entries.length, total };
  }, [selections, options]);

  const buildSheetsForOption = (option, pose, backgroundId) => {
    const imageTypes = option.imageTypes || [];
    const flat = imageTypes
      .map((imageType) => new Array(imageType.count || 1).fill(imageType))
      .reduce((acc, arr) => acc.concat(arr), []);

    return flat.map((imageType, index) => new Sheet({
      index,
      poseId: pose.id,
      pose,
      imageTypeId: imageType.id,
      imageType,
      backgroundId: imageType.requiresBg === false ? null : backgroundId,
    }));
  };

  const handleContinue = async () => {
    if (!cart) return;

    if (isUpsell && summary.count === 0) {
      history.push(routes.review());
      return;
    }

    if (!pricelist || summary.count === 0) return;
    setIsSubmitting(true);

    try {
      const entries = Object.entries(selections);

      // eslint-disable-next-line no-restricted-syntax
      for (const [studentImageId, sel] of entries) {
        const studentImage = (pastImages || []).find((si) => si.id === studentImageId);
        const option = options.find((o) => o.id === sel.optionId);
        if (!studentImage || !option) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const pose = (studentImage.poses && studentImage.poses[0]) || null;
        if (!pose) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const resolvedBgId = sel.backgroundId
          || defaultsByStudentImageId[studentImage.id]?.id
          || backgrounds[0]?.id
          || null;
        const sheets = buildSheetsForOption(option, pose, resolvedBgId);

        const orderPackage = new OrderPackage({
          cartId: cart.id,
          studentId: student.id,
          optionId: option.id,
          packageId: pricelist.id,
          apiOption: option,
          extraPoses: 0,
          sheets,
          isPreviewHidden: false,
        });

        // eslint-disable-next-line no-await-in-loop
        await orderPackage.save({ with: 'sheets' });

        if (Object.keys(orderPackage.errors || {}).length > 0) {
          throw new Error('OrderPackage save validation error');
        }
      }

      try {
        await refreshVolumeDiscount(cart.id);
      } catch (couponErr) {
        // eslint-disable-next-line no-console
        console.error('SeniorPastImagesQuickOrder: refreshVolumeDiscount failed', couponErr);
      }

      queryClient.invalidateQueries({ queryKey: [QueryKeys.cart] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.numItems] });

      setCustomer({ ...customer });
      history.push(routes.review());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('SeniorPastImagesQuickOrder: save failed', err);
      toast('There was an error adding these items to your cart. Please refresh and try again.', { type: 'error' });
      setIsSubmitting(false);
    }
  };

  if (!student) {
    history.replace(routes.studentIdEntry());
    return null;
  }

  if (isLoadingImages || isLoadingPricelist || (isFetching && (pastImages || []).length === 0)) {
    return (
      <div className={styles.container}>
        <div className={styles.spinnerWrap}><Spinner color="blue" /></div>
      </div>
    );
  }

  if (!pricelist) {
    if (isUpsell) {
      history.replace(routes.review());
      return null;
    }
    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <h1 className={styles.header}>Pictures from Previous School Years</h1>
          <div className={styles.emptyState}>
            This option isn’t available right now. Please check back soon.
          </div>
        </div>
      </div>
    );
  }

  if (!pastImages || pastImages.length === 0) {
    if (isUpsell) {
      history.replace(routes.review());
      return null;
    }
    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <h1 className={styles.header}>Pictures from Previous School Years</h1>
          <div className={styles.emptyState}>
            {`We didn’t find any past pictures for ${student.firstName}. If you think this is a mistake, please contact us.`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ToastContainer position="bottom-left" />
      {isDiscountPopupOpen && (
        <DiscountPopup onClose={() => setIsDiscountPopupOpen(false)} />
      )}
      <div className={styles.inner}>
        {/* — Grade thumbnail strip + senior portrait callout — */}
        {gradedTimeline.length > 0 && (
          <div className={styles.timelineSection}>
            <h2 className={styles.timelineHeader}>Up to 40% Off past year’s pictures</h2>
            <div className={styles.timelineStrip}>
              {gradedTimeline.map(({ studentImage, label }) => (
                <div key={studentImage.id} className={styles.timelineItem}>
                  <div className={styles.timelineThumbWrap}>
                    {studentImage.watermarkUrl && (
                      <img
                        className={styles.timelineThumb}
                        src={studentImage.watermarkUrl}
                        alt={label}
                      />
                    )}
                  </div>
                  <div className={styles.timelineLabel}>{label}</div>
                </div>
              ))}
              {seniorImage && (
                <div key="senior" className={`${styles.timelineItem} ${styles.timelineSenior}`}>
                  <div className={styles.timelineThumbWrap}>
                    {seniorImage.watermarkUrl && (
                      <img
                        className={styles.timelineThumb}
                        src={seniorImage.watermarkUrl}
                        alt="Senior"
                      />
                    )}
                    <div className={styles.timelineGradBadge}>graduate</div>
                  </div>
                  <div className={styles.timelineLabel}>Senior</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* — Grad products hero row (placeholders for now; real packages
              wire up in Phase 2.2 once the collage / magnet / announcement
              backends exist). — */}
        <div className={styles.heroSection}>
          <h2 className={styles.heroHeader}>
            {`Celebrate ${student.firstName}’s Graduation`}
          </h2>
          <div className={styles.heroGrid}>
            <div className={`${styles.heroCard} ${styles.heroCardPlaceholder}`}>
              <div className={styles.heroCardImg}>
                {seniorImage?.watermarkUrl && (
                  <img src={seniorImage.watermarkUrl} alt="School Years Collage preview" />
                )}
              </div>
              <div className={styles.heroCardName}>School Years Collage</div>
              <div className={styles.heroCardPrice}>Coming soon</div>
            </div>
            <div className={`${styles.heroCard} ${styles.heroCardPlaceholder}`}>
              <div className={styles.heroCardImg}>
                {seniorImage?.watermarkUrl && (
                  <img src={seniorImage.watermarkUrl} alt="Graduation Magnet preview" />
                )}
              </div>
              <div className={styles.heroCardName}>Graduation Magnet</div>
              <div className={styles.heroCardPrice}>Coming soon</div>
            </div>
            <div className={`${styles.heroCard} ${styles.heroCardPlaceholder}`}>
              <div className={styles.heroCardImg}>
                {seniorImage?.watermarkUrl && (
                  <img src={seniorImage.watermarkUrl} alt="Announcements preview" />
                )}
              </div>
              <div className={styles.heroCardName}>Announcements</div>
              <div className={styles.heroCardPrice}>Coming soon</div>
            </div>
          </div>
        </div>

        {/* — Past-photo individual-order grid (same UX as elementary Quick Order) — */}
        <h1 className={styles.header}>Watch your child grow up</h1>
        <p className={styles.subheader}>Every year has its memories</p>
        <p className={styles.copy}>Order today before the school year ends</p>

        <div className={styles.grid}>
          {pastImages.map((studentImage) => (
            <PastImageOption
              key={studentImage.id}
              studentImage={studentImage}
              options={options}
              backgrounds={backgrounds}
              defaultBackground={defaultsByStudentImageId[studentImage.id]}
              selection={selections[studentImage.id]}
              onSelectOption={handleSelectOption}
              onSelectBackground={handleSelectBackground}
            />
          ))}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.summary}>
            <span className={styles.summaryCount}>
              {summary.count === 0
                ? 'No pictures selected'
                : `${summary.count} ${summary.count === 1 ? 'picture' : 'pictures'} selected`}
            </span>
            {summary.count > 0 && (
              <span className={styles.summaryTotal}>{`$${summary.total.toFixed(2)}`}</span>
            )}
            {volumeDiscountPercentage(summary.count) > 0 && (
              <span className={styles.savingsPill}>
                {`You'll save ${volumeDiscountPercentage(summary.count)}%`}
              </span>
            )}
          </div>
          <button
            type="button"
            className={styles.continueBtn}
            disabled={isSubmitting || (!isUpsell && summary.count === 0)}
            onClick={handleContinue}
          >
            {isSubmitting ? 'Adding to cart...' : 'Continue to Checkout'}
          </button>
        </div>
        <div className={styles.bannerSlot}>{/* incentive banner slot */}</div>
      </div>
    </div>
  );
}
